<template>
  <v-dialog v-model="isOpen" max-width="480" persistent>
    <v-card data-cy="demo-schedule-modal">
      <v-card-title class="pa-4 d-flex align-center justify-space-between">
        <span>{{ isReschedule ? 'Reschedule Demo' : 'Schedule Demo' }}</span>
        <v-btn icon variant="text" size="small" @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <!-- Prospect summary -->
        <div class="mb-4 pa-3 bg-grey-lighten-4 rounded">
          <div class="text-caption text-grey mb-1">Prospect</div>
          <div class="text-body-2 font-weight-medium">
            {{ props.trial?.firstname }} {{ props.trial?.lastname }}
          </div>
          <div class="text-body-2">{{ props.trial?.school_name }}</div>
          <div class="text-caption text-grey">{{ props.trial?.email }}</div>
        </div>

        <!-- Existing demo date notice -->
        <v-alert
          v-if="isReschedule"
          type="info"
          density="compact"
          variant="tonal"
          class="mb-4"
        >
          Currently scheduled for {{ formatExistingDate }}. Pick a new date/time to reschedule.
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="demoDate"
            label="Demo Date & Time (Eastern Time)*"
            type="datetime-local"
            :rules="[rules.required]"
            required
            hint="All times are Eastern Time (ET)"
            persistent-hint
            data-cy="demo-date-input"
          />
        </v-form>

        <!-- Post-schedule result -->
        <div v-if="result" class="mt-4">
          <v-alert
            :type="result.warnings?.length ? 'warning' : 'success'"
            density="compact"
            variant="tonal"
          >
            <div>{{ result.message }}</div>
            <div v-if="result.zoom_join_url" class="mt-2">
              <strong>Zoom Link:</strong>
              <a :href="result.zoom_join_url" target="_blank" rel="noopener" class="ml-1">
                {{ result.zoom_join_url }}
              </a>
            </div>
            <div v-if="result.warnings?.length" class="mt-2">
              <div v-for="(w, i) in result.warnings" :key="i" class="text-caption">⚠ {{ w }}</div>
            </div>
          </v-alert>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" color="grey" @click="close" :disabled="loading">
          {{ result ? 'Close' : 'Cancel' }}
        </v-btn>
        <v-btn
          v-if="!result"
          color="primary"
          variant="flat"
          :loading="loading"
          :disabled="!valid || !demoDate"
          @click="submit"
          data-cy="schedule-demo-btn"
        >
          {{ isReschedule ? 'Reschedule' : 'Schedule Demo' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { useAlertsStore } from '@/stores/alerts';
import useTrials, { type TrialMembership } from '@/stores/support/trials';

type ScheduleDemoResult = {
  status: boolean;
  message?: string;
  data?: TrialMembership;
  zoom_join_url?: string;
  google_event_id?: string;
  warnings?: string[];
};

const props = defineProps<{
  modelValue: boolean;
  trial: TrialMembership | null;
  /** Optional override so leads page can update the leads store instead of trials */
  scheduleFn?: (trialId: string, demoDate: string) => Promise<ScheduleDemoResult>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const trialsStore = useTrials();
const alertsStore = useAlertsStore();

const form    = ref();
const valid   = ref(false);
const loading = ref(false);
const demoDate = ref('');
const result   = ref<{
  message: string;
  zoom_join_url?: string;
  google_event_id?: string;
  warnings?: string[];
} | null>(null);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const isReschedule = computed(() => !!props.trial?.demo_date);

const formatExistingDate = computed(() => {
  if (!props.trial?.demo_date) return '';
  return dayjs(props.trial.demo_date).format('MMM D, YYYY h:mm A') + ' ET';
});

// Pre-fill if rescheduling
watch(() => props.trial, (trial) => {
  result.value = null;
  if (trial?.demo_date) {
    // datetime-local input expects "YYYY-MM-DDTHH:mm"
    demoDate.value = dayjs(trial.demo_date).format('YYYY-MM-DDTHH:mm');
  } else {
    demoDate.value = '';
  }
}, { immediate: true });

const rules = {
  required: (v: string) => !!v || 'Required',
};

const submit = async () => {
  if (!form.value || !props.trial) return;
  const { valid: isValid } = await form.value.validate();
  if (!isValid) return;

  loading.value = true;
  try {
    const schedule = props.scheduleFn || trialsStore.scheduleDemo;
    const res = await schedule(props.trial.trial_id, demoDate.value);
    if (res.status) {
      result.value = {
        message:         res.message || 'Demo scheduled successfully',
        zoom_join_url:   res.zoom_join_url,
        google_event_id: res.google_event_id,
        warnings:        res.warnings,
      };
      if (res.warnings?.length) {
        alertsStore.addAlert('warning', 'Demo date saved but with some warnings — see modal for details.', 8);
      } else {
        alertsStore.addAlert('success', 'Demo scheduled — calendar invite sent.', 5);
      }
    } else {
      alertsStore.addAlert('error', res.message || 'Failed to schedule demo', 10);
    }
  } catch (e) {
    console.error('scheduleDemo error:', e);
    alertsStore.addAlert('error', 'An unexpected error occurred.', 10);
  } finally {
    loading.value = false;
  }
};

const close = () => {
  isOpen.value = false;
  setTimeout(() => {
    result.value = null;
    demoDate.value = '';
    if (form.value) form.value.resetValidation();
  }, 300);
};
</script>
